import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiMapPin, FiCommand, FiThermometer, FiWind, FiX } from 'react-icons/fi';
import { TAKE_OFF_SITES, ALL_SITES } from '../../constants';
import { getNow } from '../../services/api';
import { getWindDirection } from '../../utils';
import { getFlightSafetyStatus, getFlightStatusLabel } from '../../utils/flightSafety';
import { SearchResultSkeleton } from '../Skeleton';
import { readSavedSpots } from '../../hooks/useSavedSpots';

const GlobalSearch = ({ isOpen: externalIsOpen, onClose, onOpen }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const actualIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [weatherData, setWeatherData] = useState({});
    const [savedSpots, setSavedSpots] = useState([]);
    const inputRef = useRef(null);
    const modalRef = useRef(null);
    const resultsRef = useRef(null);

    // Detect if user is on Mac
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    // Use ALL_SITES when searching, TAKE_OFF_SITES when showing default list
    const sitesToSearch = query.trim() ? ALL_SITES : TAKE_OFF_SITES;
    
    // Filter and sort sites based on search query
    const filteredSites = sitesToSearch
        .filter(site =>
            !query.trim() || // Show all main sites when no query
            t(site.name).toLowerCase().includes(query.toLowerCase()) ||
            site.slug.toLowerCase().includes(query.toLowerCase())
        )
        .sort((a, b) => {
            const aWeather = weatherData[a.id];
            const bWeather = weatherData[b.id];
            const aStatus = getFlightSafetyStatus(a, aWeather);
            const bStatus = getFlightSafetyStatus(b, bWeather);

            // Online stations first, then offline
            if (aStatus.isOffline && !bStatus.isOffline) return 1;
            if (!aStatus.isOffline && bStatus.isOffline) return -1;

            // If both are online or both offline, sort alphabetically
            return t(a.name).localeCompare(t(b.name));
        });

    // Saved forecast locations (localStorage). Filtered by name; shown even with
    // an empty query as a quick-access favorites section above the stations.
    const filteredSpots = savedSpots.filter(
        (s) => !query.trim() || (s.name || '').toLowerCase().includes(query.toLowerCase())
    );

    // Flat, ordered list backing keyboard nav + selection: saved spots first,
    // then stations. Each row's highlight key is its index in THIS array.
    const results = [
        ...filteredSpots.map((spot) => ({ type: 'spot', key: `spot:${spot.id}`, spot })),
        ...filteredSites.map((site) => ({ type: 'site', key: `site:${site.slug}`, site })),
    ];

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Cmd+K or Ctrl+K to open search
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
                if (externalIsOpen === undefined) {
                    setIsOpen(true);
                } else if (onOpen) {
                    onOpen();
                }
            }

            // Escape to close
            if (event.key === 'Escape') {
                if (externalIsOpen !== undefined && onClose) {
                    onClose();
                } else {
                    setIsOpen(false);
                }
                setQuery('');
                setSelectedIndex(0);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [externalIsOpen, onClose, onOpen]);

    // Handle search modal keyboard navigation
    useEffect(() => {
        if (!actualIsOpen) return;

        const handleModalKeyDown = (event) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, 0));
            } else if (event.key === 'Enter') {
                event.preventDefault();
                if (results[selectedIndex]) {
                    handleSelect(results[selectedIndex]);
                }
            }
        };

        document.addEventListener('keydown', handleModalKeyDown);
        return () => document.removeEventListener('keydown', handleModalKeyDown);
    }, [actualIsOpen, results, selectedIndex]);

    // Re-read saved spots each time the modal opens. GlobalSearch is mounted
    // once at the app root and never unmounts, so a spot saved later on the
    // Forecast page wouldn't otherwise appear — read a fresh snapshot on open.
    useEffect(() => {
        if (actualIsOpen) setSavedSpots(readSavedSpots());
    }, [actualIsOpen]);

    // Focus input when modal opens and fetch weather data
    useEffect(() => {
        if (actualIsOpen && inputRef.current) {
            inputRef.current.focus();
            // Fetch weather data for all sites
            filteredSites.forEach(async (site) => {
                if (!weatherData[site.id]) {
                    try {
                        const data = await getNow(site.id);
                        setWeatherData(prev => ({
                            ...prev,
                            [site.id]: data
                        }));
                    } catch (error) {
                        console.error(`Failed to fetch weather for site ${site.id}:`, error);
                    }
                }
            });
        }
    }, [actualIsOpen, filteredSites]);

    // Reset selection when query changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    // Auto-scroll to keep selected item visible
    useEffect(() => {
        if (!resultsRef.current || !actualIsOpen) return;

        // Section headers break positional child indexing, so target the row
        // by its data-result-index (its position in the flat `results` array).
        const selectedElement = resultsRef.current.querySelector(`[data-result-index="${selectedIndex}"]`);
        if (selectedElement) {
            selectedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest'
            });
        }
    }, [selectedIndex, actualIsOpen]);

    // Handle selection of any result row (saved spot or station).
    const handleSelect = (item) => {
        if (item.type === 'spot') {
            // Saved locations open the Point Forecast at their coordinates —
            // no `site` param (they aren't stations).
            const params = new URLSearchParams({
                lat: String(item.spot.lat),
                lng: String(item.spot.lng),
                feature: 'point',
            });
            navigate(`/forecast?${params.toString()}`);
        } else {
            navigate(`/site/${item.site.slug}`);
        }
        if (externalIsOpen !== undefined && onClose) {
            onClose();
        } else {
            setIsOpen(false);
        }
        setQuery('');
        setSelectedIndex(0);
    };

    // Handle backdrop click
    const handleBackdropClick = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            if (externalIsOpen !== undefined && onClose) {
                onClose();
            } else {
                setIsOpen(false);
            }
            setQuery('');
            setSelectedIndex(0);
        }
    };

    // Handle close button click
    const handleCloseClick = () => {
        if (externalIsOpen !== undefined && onClose) {
            onClose();
        } else {
            setIsOpen(false);
        }
        setQuery('');
        setSelectedIndex(0);
    };

    if (!actualIsOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-4 sm:pt-20 px-2 sm:px-0"
            onClick={handleBackdropClick}
        >
            <div
                ref={modalRef}
                className="w-full max-w-2xl mx-2 sm:mx-4 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in slide-in-from-top-4 duration-200 max-h-[95vh] sm:max-h-none flex flex-col"
            >
                {/* Search Input */}
                <div className="flex items-center px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-200 dark:border-slate-700">
                    <FiSearch className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('Search locations & takeoff sites...')}
                        className="flex-1 text-base sm:text-lg bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    {/* Close button for mobile */}
                    <button
                        onClick={handleCloseClick}
                        className="sm:hidden p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-2"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                    {/* Desktop keyboard shortcuts */}
                    <div className="hidden sm:flex items-center space-x-2 text-xs text-gray-400 dark:text-gray-500">
                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-xs">↑↓</kbd>
                        <span>{t('to navigate')}</span>
                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-xs">↵</kbd>
                        <span>{t('to select')}</span>
                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded text-xs">esc</kbd>
                        <span>{t('to close')}</span>
                    </div>
                </div>

                {/* Search Results */}
                <div ref={resultsRef} className="flex-1 overflow-y-auto max-h-80 sm:max-h-96">
                    {results.length > 0 ? (
                        <div className="py-2">
                            {/* Saved locations — only when at least one exists.
                                Indices 0..(filteredSpots.length-1) in `results`. */}
                            {filteredSpots.length > 0 && (
                                <>
                                    <div className="px-3 sm:px-4 pt-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        {t('Saved locations')}
                                    </div>
                                    {filteredSpots.map((spot, i) => {
                                        const index = i;
                                        return (
                                            <button
                                                key={`spot:${spot.id}`}
                                                data-result-index={index}
                                                onClick={() => handleSelect({ type: 'spot', spot })}
                                                className={`w-full text-left px-3 sm:px-4 py-3 flex items-center space-x-3 transition-colors ${
                                                    index === selectedIndex
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                                                }`}
                                            >
                                                <FiMapPin className="w-4 h-4 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium truncate">{spot.name}</div>
                                                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                        {spot.lat}, {spot.lng}
                                                    </div>
                                                </div>
                                                {index === selectedIndex && (
                                                    <kbd className="hidden sm:block px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">↵</kbd>
                                                )}
                                            </button>
                                        );
                                    })}
                                </>
                            )}

                            {/* Takeoff sites. Header shown only when a saved
                                section precedes it (otherwise it's redundant).
                                Indices are offset by the saved-spot count. */}
                            {filteredSites.length > 0 && filteredSpots.length > 0 && (
                                <div className="px-3 sm:px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {t('Takeoff sites')}
                                </div>
                            )}
                            {filteredSites.map((site, j) => {
                                const index = filteredSpots.length + j;
                                const weather = weatherData[site.id];
                                const flightStatus = getFlightSafetyStatus(site, weather);

                                return (
                                    <button
                                        key={site.slug}
                                        data-result-index={index}
                                        onClick={() => handleSelect({ type: 'site', site })}
                                        className={`w-full text-left px-3 sm:px-4 py-3 flex items-center space-x-3 transition-colors ${
                                            index === selectedIndex
                                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                        <FiMapPin className="w-4 h-4 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <div className="font-medium truncate">
                                                    {t(site.name)}
                                                </div>
                                                {/* Flight status badge */}
                                                {weather && (
                                                    <div className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                                        flightStatus.status === 'good'
                                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                                            : flightStatus.status === 'danger'
                                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                                                            : flightStatus.status === 'offline'
                                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                    }`}>
                                                        {t(getFlightStatusLabel(flightStatus.status))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between mt-1">
                                                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                    {site.slug}
                                                </div>
                                                {/* Weather info */}
                                                {weather && !flightStatus.isOffline ? (
                                                    <div className="flex items-center space-x-2 sm:space-x-3 text-xs text-gray-500 dark:text-gray-400">
                                                        <div className="flex items-center space-x-1">
                                                            <FiThermometer className="w-3 h-3" />
                                                            <span className="whitespace-nowrap">{Math.round(weather.TEMPERATURE || 0)}°C</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <FiWind className="w-3 h-3" />
                                                            <span className="whitespace-nowrap">{Math.round(weather.WIND_SP || 0)} {t('m/s')}</span>
                                                            <span className="text-xs hidden sm:inline">({getWindDirection(weather.WIND_ANG)})</span>
                                                        </div>
                                                    </div>
                                                ) : weather === undefined ? (
                                                    <SearchResultSkeleton />
                                                ) : null}
                                            </div>
                                        </div>
                                        {index === selectedIndex && (
                                            <kbd className="hidden sm:block px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">↵</kbd>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                            <FiSearch className="w-8 h-8 mx-auto mb-3 opacity-50" />
                            <div className="text-lg font-medium mb-1">{t('No results found')}</div>
                            <div className="text-sm">{t('Try a different search term')}</div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-200 dark:border-slate-700">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <div className="hidden sm:flex items-center space-x-2">
                            {isMac ? (
                                <>
                                    <FiCommand className="w-3 h-3" />
                                    <span>{t('Press')} <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded text-xs">⌘ K</kbd> {t('to search')}</span>
                                </>
                            ) : (
                                <span>{t('Press')} <kbd className="px-1 py-0.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded text-xs">Ctrl K</kbd> {t('to search')}</span>
                            )}
                        </div>
                        <div className="sm:hidden text-center w-full">
                            {results.length} {t('results')}
                        </div>
                        <div className="hidden sm:block">
                            {results.length} {t('results')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobalSearch;
